import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";

const emptySelects = {
  recyclingCodes: [],
  componentTypes: [],
  materialFamilies: [],
  plasticForms: [],
  separateAbilities: [],
  sortingInstructions: [],
  reusabilityTypes: [],
  weightUnits: [],
  labelStatuses: [],
  representativeTypes: [],
  materialSubtypesByFamily: {},
};

const emptyProductSelects = {
  weightUnits: [],
  labelStatuses: [],
  representativeTypes: [],
  productCategories: [],
  fibreComponentTypes: [],
  fibreTypes: [],
  useRestrictions: [],
  minimumUserAges: [],
  careSymbols: [],
};

const initialState = {
  labels: [],
  currentLabel: null,

  countries: [],
  components: [],
  selects: { ...emptySelects },

  productLabels: [],
  currentProductLabel: null,
  productSelects: { ...emptyProductSelects },
  productFibreComponents: [],

  fetchingLabels: false,
  fetchingLabel: false,
  creatingLabel: false,
  updatingLabel: false,
  deletingLabel: false,
  generatingPackagingLabelQr: false,

  fetchingCountries: false,
  creatingCountry: false,
  updatingCountry: false,
  deletingCountry: false,

  fetchingComponents: false,
  creatingComponent: false,
  updatingComponent: false,
  deletingComponent: false,

  fetchingSelects: false,
  selectsLoaded: false,

  fetchingProductLabels: false,
  fetchingProductLabel: false,
  creatingProductLabel: false,
  updatingProductLabel: false,
  deletingProductLabel: false,
  regeneratingProductLabelQr: false,

  fetchingProductSelects: false,
  productSelectsLoaded: false,

  fetchingProductFibreComponents: false,
  creatingProductFibreComponent: false,
  updatingProductFibreComponent: false,
  deletingProductFibreComponent: false,

  labelsError: null,
  labelError: null,
  countriesError: null,
  componentsError: null,
  selectsError: null,

  productLabelsError: null,
  productLabelError: null,
  productSelectsError: null,
  productFibreComponentsError: null,
};

function asList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function upsertById(list, item) {
  if (!item || item.id == null) return list;
  const index = list.findIndex((entry) => String(entry.id) === String(item.id));
  if (index === -1) return [...list, item];
  const next = list.slice();
  next[index] = { ...list[index], ...item };
  return next;
}

function removeById(list, id) {
  return list.filter((entry) => String(entry.id) !== String(id));
}

async function requestJson(endpoint, method, payload, fallbackError) {
  const isFormData =
    typeof FormData !== "undefined" && payload instanceof FormData;
  const response = await callApi(
    endpoint,
    method,
    method === "GET" ? {} : payload ?? {},
    isFormData ? { contentType: "multipart/form-data" } : undefined
  );
  if (!response.ok) {
    const error = new Error(response.errorMessage || "Error");
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export const fetchLabelSettingsSelects = createAsyncThunk(
  "labelSettings/fetchLabelSettingsSelects",
  async (_, { rejectWithValue }) => {
    try {
      return await requestJson(
        "/label-settings/selects",
        "GET",
        undefined,
        "Failed to fetch label settings selects"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchPackagingLabels = createAsyncThunk(
  "labelSettings/fetchPackagingLabels",
  async (_, { rejectWithValue }) => {
    try {
      return await requestJson(
        "/label-settings/labels",
        "GET",
        undefined,
        "Failed to fetch packaging labels"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchPackagingLabel = createAsyncThunk(
  "labelSettings/fetchPackagingLabel",
  async (labelId, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}`,
        "GET",
        undefined,
        "Failed to fetch packaging label"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const createPackagingLabel = createAsyncThunk(
  "labelSettings/createPackagingLabel",
  async (payload, { rejectWithValue }) => {
    try {
      return await requestJson(
        "/label-settings/labels",
        "POST",
        payload ?? {},
        "Failed to create packaging label"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const updatePackagingLabel = createAsyncThunk(
  "labelSettings/updatePackagingLabel",
  async ({ labelId, changes }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}`,
        "POST",
        changes ?? {},
        "Failed to update packaging label"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const deletePackagingLabel = createAsyncThunk(
  "labelSettings/deletePackagingLabel",
  async (labelId, { rejectWithValue }) => {
    try {
      await requestJson(
        `/label-settings/labels/${labelId}`,
        "DELETE",
        undefined,
        "Failed to delete packaging label"
      );
      return { success: true, id: labelId };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const generatePackagingLabelQr = createAsyncThunk(
  "labelSettings/generatePackagingLabelQr",
  async ({ labelId }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/label-settings/labels/${labelId}/qr-code`,
        "POST",
        {}
      );
      const responseData = await response.json().catch(() => ({}));

      if (responseData?.to_buy) {
        return {
          ...responseData,
          __subscriptionRequired: true,
          __httpStatus: response.status,
        };
      }

      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }

      return responseData;
    } catch (error) {
      let errorMessage = "Error";

      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error.error) {
        errorMessage =
          typeof error.error === "string"
            ? error.error
            : JSON.stringify(error.error);
      }

      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchPackagingLabelCountries = createAsyncThunk(
  "labelSettings/fetchPackagingLabelCountries",
  async (labelId, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/countries`,
        "GET",
        undefined,
        "Failed to fetch packaging label countries"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const createPackagingLabelCountry = createAsyncThunk(
  "labelSettings/createPackagingLabelCountry",
  async ({ labelId, payload }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/countries`,
        "POST",
        payload ?? {},
        "Failed to create packaging label country"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchPackagingLabelCountry = createAsyncThunk(
  "labelSettings/fetchPackagingLabelCountry",
  async ({ labelId, countryId }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/countries/${countryId}`,
        "GET",
        undefined,
        "Failed to fetch packaging label country"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const updatePackagingLabelCountry = createAsyncThunk(
  "labelSettings/updatePackagingLabelCountry",
  async ({ labelId, countryId, changes }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/countries/${countryId}`,
        "POST",
        changes ?? {},
        "Failed to update packaging label country"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const deletePackagingLabelCountry = createAsyncThunk(
  "labelSettings/deletePackagingLabelCountry",
  async ({ labelId, countryId }, { rejectWithValue }) => {
    try {
      await requestJson(
        `/label-settings/labels/${labelId}/countries/${countryId}`,
        "DELETE",
        undefined,
        "Failed to delete packaging label country"
      );
      return { success: true, id: countryId };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchPackagingComponents = createAsyncThunk(
  "labelSettings/fetchPackagingComponents",
  async (labelId, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/components`,
        "GET",
        undefined,
        "Failed to fetch packaging components"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const createPackagingComponent = createAsyncThunk(
  "labelSettings/createPackagingComponent",
  async ({ labelId, payload }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/components`,
        "POST",
        payload ?? {},
        "Failed to create packaging component"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchPackagingComponent = createAsyncThunk(
  "labelSettings/fetchPackagingComponent",
  async ({ labelId, componentId }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/components/${componentId}`,
        "GET",
        undefined,
        "Failed to fetch packaging component"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const updatePackagingComponent = createAsyncThunk(
  "labelSettings/updatePackagingComponent",
  async ({ labelId, componentId, changes }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/components/${componentId}`,
        "POST",
        changes ?? {},
        "Failed to update packaging component"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const deletePackagingComponent = createAsyncThunk(
  "labelSettings/deletePackagingComponent",
  async ({ labelId, componentId }, { rejectWithValue }) => {
    try {
      await requestJson(
        `/label-settings/labels/${labelId}/components/${componentId}`,
        "DELETE",
        undefined,
        "Failed to delete packaging component"
      );
      return { success: true, id: componentId };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchProductLabelSelects = createAsyncThunk(
  "labelSettings/fetchProductLabelSelects",
  async (_, { rejectWithValue }) => {
    try {
      return await requestJson(
        "/label-settings/selects?type=product",
        "GET",
        undefined,
        "Failed to fetch product label selects"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchProductLabels = createAsyncThunk(
  "labelSettings/fetchProductLabels",
  async (_, { rejectWithValue }) => {
    try {
      return await requestJson(
        "/label-settings/product-labels",
        "GET",
        undefined,
        "Failed to fetch product labels"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchProductLabel = createAsyncThunk(
  "labelSettings/fetchProductLabel",
  async (labelId, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/product-labels/${labelId}`,
        "GET",
        undefined,
        "Failed to fetch product label"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const createProductLabel = createAsyncThunk(
  "labelSettings/createProductLabel",
  async (payload, { rejectWithValue }) => {
    try {
      return await requestJson(
        "/label-settings/product-labels",
        "POST",
        payload ?? {},
        "Failed to create product label"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const updateProductLabel = createAsyncThunk(
  "labelSettings/updateProductLabel",
  async ({ labelId, changes }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/product-labels/${labelId}`,
        "POST",
        changes ?? {},
        "Failed to update product label"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const deleteProductLabel = createAsyncThunk(
  "labelSettings/deleteProductLabel",
  async (labelId, { rejectWithValue }) => {
    try {
      await requestJson(
        `/label-settings/product-labels/${labelId}`,
        "DELETE",
        undefined,
        "Failed to delete product label"
      );
      return { success: true, id: labelId };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const regenerateProductLabelQr = createAsyncThunk(
  "labelSettings/regenerateProductLabelQr",
  async ({ labelId }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/label-settings/product-labels/${labelId}/qr-code`,
        "POST",
        {}
      );
      const responseData = await response.json().catch(() => ({}));

      if (responseData?.to_buy) {
        return {
          ...responseData,
          __subscriptionRequired: true,
          __httpStatus: response.status,
        };
      }

      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }

      return responseData;
    } catch (error) {
      let errorMessage = "Error";

      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error.error) {
        errorMessage =
          typeof error.error === "string"
            ? error.error
            : JSON.stringify(error.error);
      }

      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchProductLabelFibreComponents = createAsyncThunk(
  "labelSettings/fetchProductLabelFibreComponents",
  async ({ productLabelId }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${productLabelId}/fibre-components`,
        "GET",
        undefined,
        "Failed to fetch product label fibre components"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const createProductLabelFibreComponent = createAsyncThunk(
  "labelSettings/createProductLabelFibreComponent",
  async ({ productLabelId, component }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${productLabelId}/fibre-components`,
        "POST",
        component ?? {},
        "Failed to create product label fibre component"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const updateProductLabelFibreComponent = createAsyncThunk(
  "labelSettings/updateProductLabelFibreComponent",
  async ({ productLabelId, componentId, component }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${productLabelId}/fibre-components/${componentId}`,
        "POST",
        component ?? {},
        "Failed to update product label fibre component"
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const deleteProductLabelFibreComponent = createAsyncThunk(
  "labelSettings/deleteProductLabelFibreComponent",
  async ({ productLabelId, componentId }, { rejectWithValue }) => {
    try {
      const result = await requestJson(
        `/label-settings/labels/${productLabelId}/fibre-components/${componentId}`,
        "DELETE",
        undefined,
        "Failed to delete product label fibre component"
      );
      // Confirmed body: { success: true }. Local/Redux removal uses meta.arg.componentId.
      return result ?? { success: true };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

function asFibreComponentEntity(payload) {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  if (payload.id == null || payload.id === "") {
    return null;
  }
  return payload;
}

function normalizeProductSelects(payload) {
  const source =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? payload
      : {};
  const next = { ...source };
  Object.keys(emptyProductSelects).forEach((key) => {
    next[key] = Array.isArray(source[key]) ? source[key] : [];
  });
  return next;
}

const labelSettingsSlice = createSlice({
  name: "labelSettings",
  initialState,
  reducers: {
    clearLabelsError: (state) => {
      state.labelsError = null;
    },
    clearLabelError: (state) => {
      state.labelError = null;
    },
    clearCountriesError: (state) => {
      state.countriesError = null;
    },
    clearComponentsError: (state) => {
      state.componentsError = null;
    },
    clearSelectsError: (state) => {
      state.selectsError = null;
    },
    clearCurrentLabel: (state) => {
      state.currentLabel = null;
      state.countries = [];
      state.components = [];
    },
    clearProductLabelsError: (state) => {
      state.productLabelsError = null;
    },
    clearProductLabelError: (state) => {
      state.productLabelError = null;
    },
    clearProductSelectsError: (state) => {
      state.productSelectsError = null;
    },
    clearProductFibreComponentsError: (state) => {
      state.productFibreComponentsError = null;
    },
    clearProductFibreComponents: (state) => {
      state.productFibreComponents = [];
      state.productFibreComponentsError = null;
    },
    clearCurrentProductLabel: (state) => {
      state.currentProductLabel = null;
      state.productLabelError = null;
      state.productFibreComponents = [];
      state.productFibreComponentsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabelSettingsSelects.pending, (state) => {
        state.fetchingSelects = true;
        state.selectsError = null;
      })
      .addCase(fetchLabelSettingsSelects.fulfilled, (state, action) => {
        state.fetchingSelects = false;
        const payload =
          action.payload && typeof action.payload === "object"
            ? action.payload
            : {};
        const materialSubtypesByFamily =
          payload.materialSubtypesByFamily &&
          typeof payload.materialSubtypesByFamily === "object" &&
          !Array.isArray(payload.materialSubtypesByFamily)
            ? payload.materialSubtypesByFamily
            : {};
        state.selects = {
          recyclingCodes: Array.isArray(payload.recyclingCodes)
            ? payload.recyclingCodes
            : [],
          componentTypes: Array.isArray(payload.componentTypes)
            ? payload.componentTypes
            : [],
          materialFamilies: Array.isArray(payload.materialFamilies)
            ? payload.materialFamilies
            : [],
          plasticForms: Array.isArray(payload.plasticForms)
            ? payload.plasticForms
            : [],
          separateAbilities: Array.isArray(payload.separateAbilities)
            ? payload.separateAbilities
            : [],
          sortingInstructions: Array.isArray(payload.sortingInstructions)
            ? payload.sortingInstructions
            : [],
          reusabilityTypes: Array.isArray(payload.reusabilityTypes)
            ? payload.reusabilityTypes
            : [],
          weightUnits: Array.isArray(payload.weightUnits)
            ? payload.weightUnits
            : [],
          labelStatuses: Array.isArray(payload.labelStatuses)
            ? payload.labelStatuses
            : [],
          representativeTypes: Array.isArray(payload.representativeTypes)
            ? payload.representativeTypes
            : [],
          materialSubtypesByFamily,
        };
        state.selectsLoaded = true;
        state.selectsError = null;
      })
      .addCase(fetchLabelSettingsSelects.rejected, (state, action) => {
        state.fetchingSelects = false;
        state.selectsError =
          action.payload || "Failed to fetch label settings selects";
      })

      .addCase(fetchPackagingLabels.pending, (state) => {
        state.fetchingLabels = true;
        state.labelsError = null;
      })
      .addCase(fetchPackagingLabels.fulfilled, (state, action) => {
        state.fetchingLabels = false;
        state.labels = asList(action.payload);
        state.labelsError = null;
      })
      .addCase(fetchPackagingLabels.rejected, (state, action) => {
        state.fetchingLabels = false;
        state.labelsError =
          action.payload || "Failed to fetch packaging labels";
      })

      .addCase(fetchPackagingLabel.pending, (state) => {
        state.fetchingLabel = true;
        state.labelError = null;
      })
      .addCase(fetchPackagingLabel.fulfilled, (state, action) => {
        state.fetchingLabel = false;
        state.currentLabel = action.payload || null;
        if (action.payload) {
          state.labels = upsertById(state.labels, action.payload);
        }
        state.labelError = null;
      })
      .addCase(fetchPackagingLabel.rejected, (state, action) => {
        state.fetchingLabel = false;
        state.labelError =
          action.payload || "Failed to fetch packaging label";
      })

      .addCase(createPackagingLabel.pending, (state) => {
        state.creatingLabel = true;
        state.labelError = null;
      })
      .addCase(createPackagingLabel.fulfilled, (state, action) => {
        state.creatingLabel = false;
        state.currentLabel = action.payload || null;
        if (action.payload) {
          state.labels = upsertById(state.labels, action.payload);
        }
        state.labelError = null;
      })
      .addCase(createPackagingLabel.rejected, (state, action) => {
        state.creatingLabel = false;
        state.labelError =
          action.payload || "Failed to create packaging label";
      })

      .addCase(updatePackagingLabel.pending, (state) => {
        state.updatingLabel = true;
        state.labelError = null;
      })
      .addCase(updatePackagingLabel.fulfilled, (state, action) => {
        state.updatingLabel = false;
        if (action.payload) {
          if (
            state.currentLabel &&
            String(state.currentLabel.id) === String(action.payload.id)
          ) {
            state.currentLabel = { ...state.currentLabel, ...action.payload };
          } else if (
            state.currentLabel &&
            String(state.currentLabel.id) ===
              String(action.meta?.arg?.labelId)
          ) {
            state.currentLabel = { ...state.currentLabel, ...action.payload };
          }
          state.labels = upsertById(state.labels, action.payload);
        }
        state.labelError = null;
      })
      .addCase(updatePackagingLabel.rejected, (state, action) => {
        state.updatingLabel = false;
        state.labelError =
          action.payload || "Failed to update packaging label";
      })

      .addCase(deletePackagingLabel.pending, (state) => {
        state.deletingLabel = true;
        state.labelError = null;
      })
      .addCase(deletePackagingLabel.fulfilled, (state, action) => {
        state.deletingLabel = false;
        const deletedId = action.payload?.id ?? action.meta?.arg;
        state.labels = removeById(state.labels, deletedId);
        if (
          state.currentLabel &&
          String(state.currentLabel.id) === String(deletedId)
        ) {
          state.currentLabel = null;
          state.countries = [];
          state.components = [];
        }
        state.labelError = null;
      })
      .addCase(deletePackagingLabel.rejected, (state, action) => {
        state.deletingLabel = false;
        state.labelError =
          action.payload || "Failed to delete packaging label";
      })

      .addCase(generatePackagingLabelQr.pending, (state) => {
        state.generatingPackagingLabelQr = true;
        state.labelError = null;
      })
      .addCase(generatePackagingLabelQr.fulfilled, (state, action) => {
        state.generatingPackagingLabelQr = false;
        const payload = action.payload;
        if (payload?.__subscriptionRequired) {
          state.labelError = null;
          return;
        }
        const labelId = action.meta?.arg?.labelId;
        if (payload && typeof payload === "object") {
          if (
            state.currentLabel &&
            (payload.id == null ||
              String(state.currentLabel.id) === String(payload.id) ||
              String(state.currentLabel.id) === String(labelId))
          ) {
            state.currentLabel = {
              ...state.currentLabel,
              ...payload,
            };
          }
          if (payload.id != null) {
            state.labels = upsertById(state.labels, payload);
          } else if (
            labelId != null &&
            labelId !== "" &&
            state.currentLabel &&
            String(state.currentLabel.id) === String(labelId)
          ) {
            state.labels = upsertById(state.labels, state.currentLabel);
          }
        }
        state.labelError = null;
      })
      .addCase(generatePackagingLabelQr.rejected, (state, action) => {
        state.generatingPackagingLabelQr = false;
        state.labelError =
          action.payload || "Failed to generate packaging label QR code";
      })

      .addCase(fetchPackagingLabelCountries.pending, (state) => {
        state.fetchingCountries = true;
        state.countriesError = null;
      })
      .addCase(fetchPackagingLabelCountries.fulfilled, (state, action) => {
        state.fetchingCountries = false;
        state.countries = asList(action.payload);
        state.countriesError = null;
      })
      .addCase(fetchPackagingLabelCountries.rejected, (state, action) => {
        state.fetchingCountries = false;
        state.countriesError =
          action.payload || "Failed to fetch packaging label countries";
      })

      .addCase(createPackagingLabelCountry.pending, (state) => {
        state.creatingCountry = true;
        state.countriesError = null;
      })
      .addCase(createPackagingLabelCountry.fulfilled, (state, action) => {
        state.creatingCountry = false;
        if (action.payload) {
          state.countries = upsertById(state.countries, action.payload);
        }
        state.countriesError = null;
      })
      .addCase(createPackagingLabelCountry.rejected, (state, action) => {
        state.creatingCountry = false;
        state.countriesError =
          action.payload || "Failed to create packaging label country";
      })

      .addCase(fetchPackagingLabelCountry.pending, (state) => {
        state.fetchingCountries = true;
        state.countriesError = null;
      })
      .addCase(fetchPackagingLabelCountry.fulfilled, (state, action) => {
        state.fetchingCountries = false;
        if (action.payload) {
          state.countries = upsertById(state.countries, action.payload);
        }
        state.countriesError = null;
      })
      .addCase(fetchPackagingLabelCountry.rejected, (state, action) => {
        state.fetchingCountries = false;
        state.countriesError =
          action.payload || "Failed to fetch packaging label country";
      })

      .addCase(updatePackagingLabelCountry.pending, (state) => {
        state.updatingCountry = true;
        state.countriesError = null;
      })
      .addCase(updatePackagingLabelCountry.fulfilled, (state, action) => {
        state.updatingCountry = false;
        if (action.payload) {
          state.countries = upsertById(state.countries, action.payload);
        }
        state.countriesError = null;
      })
      .addCase(updatePackagingLabelCountry.rejected, (state, action) => {
        state.updatingCountry = false;
        state.countriesError =
          action.payload || "Failed to update packaging label country";
      })

      .addCase(deletePackagingLabelCountry.pending, (state) => {
        state.deletingCountry = true;
        state.countriesError = null;
      })
      .addCase(deletePackagingLabelCountry.fulfilled, (state, action) => {
        state.deletingCountry = false;
        const deletedId =
          action.payload?.id ?? action.meta?.arg?.countryId;
        state.countries = removeById(state.countries, deletedId);
        state.countriesError = null;
      })
      .addCase(deletePackagingLabelCountry.rejected, (state, action) => {
        state.deletingCountry = false;
        state.countriesError =
          action.payload || "Failed to delete packaging label country";
      })

      .addCase(fetchPackagingComponents.pending, (state) => {
        state.fetchingComponents = true;
        state.componentsError = null;
      })
      .addCase(fetchPackagingComponents.fulfilled, (state, action) => {
        state.fetchingComponents = false;
        state.components = asList(action.payload);
        state.componentsError = null;
      })
      .addCase(fetchPackagingComponents.rejected, (state, action) => {
        state.fetchingComponents = false;
        state.componentsError =
          action.payload || "Failed to fetch packaging components";
      })

      .addCase(createPackagingComponent.pending, (state) => {
        state.creatingComponent = true;
        state.componentsError = null;
      })
      .addCase(createPackagingComponent.fulfilled, (state, action) => {
        state.creatingComponent = false;
        if (action.payload) {
          state.components = upsertById(state.components, action.payload);
        }
        state.componentsError = null;
      })
      .addCase(createPackagingComponent.rejected, (state, action) => {
        state.creatingComponent = false;
        state.componentsError =
          action.payload || "Failed to create packaging component";
      })

      .addCase(fetchPackagingComponent.pending, (state) => {
        state.fetchingComponents = true;
        state.componentsError = null;
      })
      .addCase(fetchPackagingComponent.fulfilled, (state, action) => {
        state.fetchingComponents = false;
        if (action.payload) {
          state.components = upsertById(state.components, action.payload);
        }
        state.componentsError = null;
      })
      .addCase(fetchPackagingComponent.rejected, (state, action) => {
        state.fetchingComponents = false;
        state.componentsError =
          action.payload || "Failed to fetch packaging component";
      })

      .addCase(updatePackagingComponent.pending, (state) => {
        state.updatingComponent = true;
        state.componentsError = null;
      })
      .addCase(updatePackagingComponent.fulfilled, (state, action) => {
        state.updatingComponent = false;
        if (action.payload) {
          state.components = upsertById(state.components, action.payload);
        }
        state.componentsError = null;
      })
      .addCase(updatePackagingComponent.rejected, (state, action) => {
        state.updatingComponent = false;
        state.componentsError =
          action.payload || "Failed to update packaging component";
      })

      .addCase(deletePackagingComponent.pending, (state) => {
        state.deletingComponent = true;
        state.componentsError = null;
      })
      .addCase(deletePackagingComponent.fulfilled, (state, action) => {
        state.deletingComponent = false;
        const deletedId =
          action.payload?.id ?? action.meta?.arg?.componentId;
        state.components = removeById(state.components, deletedId);
        state.componentsError = null;
      })
      .addCase(deletePackagingComponent.rejected, (state, action) => {
        state.deletingComponent = false;
        state.componentsError =
          action.payload || "Failed to delete packaging component";
      })

      .addCase(fetchProductLabelSelects.pending, (state) => {
        state.fetchingProductSelects = true;
        state.productSelectsError = null;
      })
      .addCase(fetchProductLabelSelects.fulfilled, (state, action) => {
        state.fetchingProductSelects = false;
        state.productSelects = normalizeProductSelects(action.payload);
        state.productSelectsLoaded = true;
        state.productSelectsError = null;
      })
      .addCase(fetchProductLabelSelects.rejected, (state, action) => {
        state.fetchingProductSelects = false;
        state.productSelectsError =
          action.payload || "Failed to fetch product label selects";
      })

      .addCase(fetchProductLabels.pending, (state) => {
        state.fetchingProductLabels = true;
        state.productLabelsError = null;
      })
      .addCase(fetchProductLabels.fulfilled, (state, action) => {
        state.fetchingProductLabels = false;
        state.productLabels = asList(action.payload);
        state.productLabelsError = null;
      })
      .addCase(fetchProductLabels.rejected, (state, action) => {
        state.fetchingProductLabels = false;
        state.productLabelsError =
          action.payload || "Failed to fetch product labels";
      })

      .addCase(fetchProductLabel.pending, (state) => {
        state.fetchingProductLabel = true;
        state.productLabelError = null;
      })
      .addCase(fetchProductLabel.fulfilled, (state, action) => {
        state.fetchingProductLabel = false;
        state.currentProductLabel = action.payload || null;
        if (action.payload) {
          state.productLabels = upsertById(
            state.productLabels,
            action.payload
          );
        }
        state.productLabelError = null;
      })
      .addCase(fetchProductLabel.rejected, (state, action) => {
        state.fetchingProductLabel = false;
        state.productLabelError =
          action.payload || "Failed to fetch product label";
      })

      .addCase(createProductLabel.pending, (state) => {
        state.creatingProductLabel = true;
        state.productLabelError = null;
      })
      .addCase(createProductLabel.fulfilled, (state, action) => {
        state.creatingProductLabel = false;
        state.currentProductLabel = action.payload || null;
        if (action.payload) {
          state.productLabels = upsertById(
            state.productLabels,
            action.payload
          );
        }
        state.productLabelError = null;
      })
      .addCase(createProductLabel.rejected, (state, action) => {
        state.creatingProductLabel = false;
        state.productLabelError =
          action.payload || "Failed to create product label";
      })

      .addCase(updateProductLabel.pending, (state) => {
        state.updatingProductLabel = true;
        state.productLabelError = null;
      })
      .addCase(updateProductLabel.fulfilled, (state, action) => {
        state.updatingProductLabel = false;
        if (action.payload) {
          if (
            state.currentProductLabel &&
            String(state.currentProductLabel.id) === String(action.payload.id)
          ) {
            state.currentProductLabel = {
              ...state.currentProductLabel,
              ...action.payload,
            };
          } else if (
            state.currentProductLabel &&
            String(state.currentProductLabel.id) ===
              String(action.meta?.arg?.labelId)
          ) {
            state.currentProductLabel = {
              ...state.currentProductLabel,
              ...action.payload,
            };
          }
          state.productLabels = upsertById(
            state.productLabels,
            action.payload
          );
        }
        state.productLabelError = null;
      })
      .addCase(updateProductLabel.rejected, (state, action) => {
        state.updatingProductLabel = false;
        state.productLabelError =
          action.payload || "Failed to update product label";
      })

      .addCase(deleteProductLabel.pending, (state) => {
        state.deletingProductLabel = true;
        state.productLabelError = null;
      })
      .addCase(deleteProductLabel.fulfilled, (state, action) => {
        state.deletingProductLabel = false;
        const deletedId = action.payload?.id ?? action.meta?.arg;
        state.productLabels = removeById(state.productLabels, deletedId);
        if (
          state.currentProductLabel &&
          String(state.currentProductLabel.id) === String(deletedId)
        ) {
          state.currentProductLabel = null;
        }
        state.productLabelError = null;
      })
      .addCase(deleteProductLabel.rejected, (state, action) => {
        state.deletingProductLabel = false;
        state.productLabelError =
          action.payload || "Failed to delete product label";
      })

      .addCase(regenerateProductLabelQr.pending, (state) => {
        state.regeneratingProductLabelQr = true;
        state.productLabelError = null;
      })
      .addCase(regenerateProductLabelQr.fulfilled, (state, action) => {
        state.regeneratingProductLabelQr = false;
        const payload = action.payload;
        if (payload?.__subscriptionRequired) {
          state.productLabelError = null;
          return;
        }
        const labelId = action.meta?.arg?.labelId;
        if (payload && typeof payload === "object") {
          if (
            state.currentProductLabel &&
            (payload.id == null ||
              String(state.currentProductLabel.id) === String(payload.id) ||
              String(state.currentProductLabel.id) === String(labelId))
          ) {
            state.currentProductLabel = {
              ...state.currentProductLabel,
              ...payload,
            };
          }
          if (payload.id != null) {
            state.productLabels = upsertById(state.productLabels, payload);
          } else if (
            labelId != null &&
            labelId !== "" &&
            state.currentProductLabel &&
            String(state.currentProductLabel.id) === String(labelId)
          ) {
            state.productLabels = upsertById(
              state.productLabels,
              state.currentProductLabel
            );
          }
        }
        state.productLabelError = null;
      })
      .addCase(regenerateProductLabelQr.rejected, (state, action) => {
        state.regeneratingProductLabelQr = false;
        state.productLabelError =
          action.payload || "Failed to regenerate product label QR code";
      })

      .addCase(fetchProductLabelFibreComponents.pending, (state) => {
        state.fetchingProductFibreComponents = true;
        state.productFibreComponentsError = null;
      })
      .addCase(fetchProductLabelFibreComponents.fulfilled, (state, action) => {
        state.fetchingProductFibreComponents = false;
        state.productFibreComponents = asList(action.payload);
        state.productFibreComponentsError = null;
      })
      .addCase(fetchProductLabelFibreComponents.rejected, (state, action) => {
        state.fetchingProductFibreComponents = false;
        state.productFibreComponentsError =
          action.payload || "Failed to fetch product label fibre components";
      })

      .addCase(createProductLabelFibreComponent.pending, (state) => {
        state.creatingProductFibreComponent = true;
        state.productFibreComponentsError = null;
      })
      .addCase(createProductLabelFibreComponent.fulfilled, (state, action) => {
        state.creatingProductFibreComponent = false;
        const entity = asFibreComponentEntity(action.payload);
        if (entity) {
          state.productFibreComponents = upsertById(
            state.productFibreComponents,
            entity
          );
        }
        state.productFibreComponentsError = null;
      })
      .addCase(createProductLabelFibreComponent.rejected, (state, action) => {
        state.creatingProductFibreComponent = false;
        state.productFibreComponentsError =
          action.payload || "Failed to create product label fibre component";
      })

      .addCase(updateProductLabelFibreComponent.pending, (state) => {
        state.updatingProductFibreComponent = true;
        state.productFibreComponentsError = null;
      })
      .addCase(updateProductLabelFibreComponent.fulfilled, (state, action) => {
        state.updatingProductFibreComponent = false;
        const entity = asFibreComponentEntity(action.payload);
        if (entity) {
          state.productFibreComponents = upsertById(
            state.productFibreComponents,
            entity
          );
        }
        state.productFibreComponentsError = null;
      })
      .addCase(updateProductLabelFibreComponent.rejected, (state, action) => {
        state.updatingProductFibreComponent = false;
        state.productFibreComponentsError =
          action.payload || "Failed to update product label fibre component";
      })

      .addCase(deleteProductLabelFibreComponent.pending, (state) => {
        state.deletingProductFibreComponent = true;
        state.productFibreComponentsError = null;
      })
      .addCase(deleteProductLabelFibreComponent.fulfilled, (state, action) => {
        state.deletingProductFibreComponent = false;
        // Confirmed delete body is { success: true }; remove by requested id.
        const deletedId = action.meta?.arg?.componentId;
        state.productFibreComponents = removeById(
          state.productFibreComponents,
          deletedId
        );
        state.productFibreComponentsError = null;
      })
      .addCase(deleteProductLabelFibreComponent.rejected, (state, action) => {
        state.deletingProductFibreComponent = false;
        state.productFibreComponentsError =
          action.payload || "Failed to delete product label fibre component";
      });
  },
});

export const {
  clearLabelsError,
  clearLabelError,
  clearCountriesError,
  clearComponentsError,
  clearSelectsError,
  clearCurrentLabel,
  clearProductLabelsError,
  clearProductLabelError,
  clearProductSelectsError,
  clearProductFibreComponentsError,
  clearProductFibreComponents,
  clearCurrentProductLabel,
} = labelSettingsSlice.actions;

export default labelSettingsSlice.reducer;
